import { useState, useEffect, useReducer } from "react";
import axios from "axios";

// functions
import { appdataReducer, initialAppdata } from "./functions/appdataReducer.js";
import {
  AppdataContext,
  AppdataDispatchContext,
} from "./functions/appdataContext.js";
import { handleAbortSwishPayment } from "./functions/swishCalls";
import { getDefaultAmount } from "./functions/getDefaultAmount.js";

// components
import Modal from "./components/Modal/Modal";
import Icon from "./components/Icon/Icon";
import Form from "./components/Form/Form";
import ThankYouScreen from "./components/ThankYouScreen/ThankYouScreen.js";
import SwishPoll from "./components/SwishPoll/SwishPoll";

// styles
import styles from "./App.module.scss";

async function getSha256(value) {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    window.crypto.subtle &&
    window.crypto.subtle.digest
  ) {
    const msgUint8 = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }
}

function App() {
  const [appdata, dispatch] = useReducer(appdataReducer, initialAppdata);
  const { initialServerData, swish } = appdata;
  const [isModalActive, setIsModalActive] = useState(false);
  const [isPollModalActive, setIsPollModalActive] = useState(false);
  const status = swish.requestState;

  const handleClosePoll = () => {
    console.log("Abort swish polling");
    // If swish status is polling or requesting - abort this process
    if (status !== "success-swish") {
      handleAbortSwishPayment(dispatch);
    }
    // Close Poll Modal
    setIsPollModalActive(false);
  };

  // Get initial data
  useEffect(() => {
    const reactAppData = window.ktk_donations || {};
    dispatch({
      type: "setBackendSetup",
      value: reactAppData,
    });
    const { ajax_url, nonce } = reactAppData;

    let form_data = new FormData();
    form_data.set("action", "get_swish_modal_data");
    form_data.set("nonce", nonce);

    axios
      .post(ajax_url, form_data)
      .then((res) => {
        //console.log(res);
        dispatch({
          type: "setFormData",
          value: {
            amount: getDefaultAmount(res.data.data.amounts),
            phone: "",
          },
        });
        dispatch({
          type: "setInitialServerData",
          value: res.data?.data,
        });
      })
      .catch((err) => {
        //console.log(err);
        dispatch({
          type: "setFormData",
          value: {
            amount: "1",
            phone: "",
          },
        });
        dispatch({
          type: "setInitialServerData",
          value: {
            amounts: [
              {
                amount: "1",
                is_default: true,
              },
              {
                amount: "175",
                is_default: false,
              },
            ],
          },
        });
      });
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (status === "success-swish") {
        try {
          const phoneWithoutPlus = appdata?.formData?.phone
            ?.replace?.(/\D/g, "")
            ?.replace(/^0/, "46");
          const phoneWithPlus =
            phoneWithoutPlus && phoneWithoutPlus !== ""
              ? `+${phoneWithoutPlus}`
              : null;
          const phoneData = phoneWithPlus
            ? {
                phone_number_plus: phoneWithPlus,
                phone_number_plus_sha256: await getSha256(phoneWithPlus),
                phone_number_noplus: phoneWithoutPlus,
                phone_number_noplus_sha256: await getSha256(phoneWithoutPlus),
              }
            : {
                phone_number_plus: null,
                phone_number_plus_sha256: null,
                phone_number_noplus: null,
                phone_number_noplus_sha256: null,
              };
          if (window.dataLayer) {
            window.dataLayer.push({
              event: "swishagava",
              ...phoneData,
              swish_amount: appdata.amount,
              data: appdata,
            });
          }
        } catch (error) {
          console.error("Error in swish polling", error);
        }
      }
    }
    fetchData();
  }, [status, appdata]);

  if (!initialServerData) {
    return null;
  }

  return (
    <AppdataContext.Provider value={appdata}>
      <AppdataDispatchContext.Provider value={dispatch}>
        <div className={styles["app"]}>
          <button
            className={styles["swish-button"]}
            onClick={() => setIsModalActive(true)}
          >
            Swisha gåva
          </button>

          <Modal
            isActive={isModalActive}
            closeModal={() => {
              setIsModalActive(false);
              handleAbortSwishPayment(dispatch);
            }}
          >
            {status === "success-swish" ? (
              <ThankYouScreen />
            ) : (
              <Form setIsPollModalActive={setIsPollModalActive} />
            )}
            <div className={styles["modal-footer"]}>
              <Icon
                className={styles["modal-swish-logo"]}
                variant="swish-symbol-figma"
              />
              <Icon
                className={styles["modal-90-logo"]}
                variant="svensk-insamlingskontroll"
              />
            </div>
          </Modal>
          <Modal
            className={styles["poll-modal"]}
            isActive={isPollModalActive}
            closeModal={handleClosePoll}
            showCloseButton={false}
            variant="white"
            hideOverlay
            small
          >
            <SwishPoll status={status} closeModal={handleClosePoll} />
          </Modal>
        </div>
      </AppdataDispatchContext.Provider>
    </AppdataContext.Provider>
  );
}

export default App;
